import { existsSync, readFileSync, writeFileSync } from "node:fs";
import path from "node:path";
import { z } from "zod";
import type pino from "pino";

/**
 * Per-user Claude Code OAuth tokens for shared machines.
 *
 * When a guest (an org-shared user who is not the machine owner) runs a Claude
 * Code agent on someone else's machine, the agent must authenticate under the
 * guest's own Claude account — not the owner's. We achieve this with a
 * long-lived OAuth token the guest generates on their own computer
 * (`claude setup-token`) and pastes into Appostle once. The daemon injects it as
 * `CLAUDE_CODE_OAUTH_TOKEN` at agent spawn.
 *
 * Tokens are secrets. They live only on the machine where they are used, keyed
 * by the connection's *verified* userId (never a self-declared username), and
 * are NEVER sent to the auth-server. On-disk shape mirrors `daemon-keypair.ts`:
 * a single JSON file in `$APPOSTLE_HOME`, written 0o600, Zod-validated on load.
 */

const StoredTokenSchema = z.object({
  token: z.string().min(1),
  addedAt: z.string().min(1),
});

const TokenFileSchema = z.object({
  v: z.literal(1),
  tokens: z.record(z.string(), StoredTokenSchema),
});

type TokenFile = z.infer<typeof TokenFileSchema>;

export interface StoredClaudeToken {
  token: string;
  addedAt: string;
}

const TOKENS_FILENAME = "claude-tokens.json";

export class ClaudeTokenStore {
  private readonly filePath: string;
  private readonly log?: pino.Logger;
  private data: TokenFile;

  constructor(appostleHome: string, logger?: pino.Logger) {
    this.filePath = path.join(appostleHome, TOKENS_FILENAME);
    this.log = logger?.child({ module: "claude-token-store" });
    this.data = this.load();
  }

  private load(): TokenFile {
    if (!existsSync(this.filePath)) {
      return { v: 1, tokens: {} };
    }
    try {
      const raw = readFileSync(this.filePath, "utf8");
      return TokenFileSchema.parse(JSON.parse(raw));
    } catch (error) {
      this.log?.warn(
        { err: error, filePath: this.filePath },
        "Failed to load claude tokens, starting empty",
      );
      return { v: 1, tokens: {} };
    }
  }

  private persist(): void {
    writeFileSync(this.filePath, JSON.stringify(this.data, null, 2) + "\n", { mode: 0o600 });
  }

  /** Return the stored token for a verified userId, or null. */
  get(userId: string): StoredClaudeToken | null {
    return this.data.tokens[userId] ?? null;
  }

  /** Store (or replace) the token for a verified userId. */
  set(userId: string, token: string): void {
    this.data.tokens[userId] = { token, addedAt: new Date().toISOString() };
    this.persist();
    this.log?.info({ userId }, "stored claude token");
  }

  /** Remove the token for a verified userId. No-op if absent. */
  remove(userId: string): void {
    if (this.data.tokens[userId] === undefined) return;
    delete this.data.tokens[userId];
    this.persist();
    this.log?.info({ userId }, "removed claude token");
  }
}
